import { withRequestDiagnostics } from "./boundary";
// Promise exits for an Effect-typed web API intent. A new adapter returns the
// Effect from `runWebIntent` and leaves it unrun. A caller that branches
// materializes an `Either`. A query function passes that Effect to
// `queryResult`. `runIntent`, `runIntentResult`, and `runIntentAsError`
// execute the Effect immediately and are deprecated.
import { Cause, Effect, Either, Exit, Option } from "effect";

const failureTag = (failure: unknown): string | undefined => {
  if (typeof failure !== "object" || failure === null || !("_tag" in failure)) {
    return undefined;
  }
  return typeof failure._tag === "string" ? failure._tag : undefined;
};

const failureStatus = (failure: unknown, tag: string | undefined) => {
  if (typeof failure === "object" && failure !== null) {
    if ("status" in failure && typeof failure.status === "number") {
      return failure.status;
    }
    if (
      "response" in failure &&
      typeof failure.response === "object" &&
      failure.response !== null &&
      "status" in failure.response &&
      typeof failure.response.status === "number"
    ) {
      return failure.response.status;
    }
  }
  if (tag === "Forbidden") return 403;
  // A write based on an older version than the server holds is the server's
  // final word on that payload; retrying it unchanged cannot succeed.
  if (tag?.endsWith("VersionConflict")) return 409;
  // An update of a row the server no longer has: no retry brings it back.
  if (tag?.endsWith("Gone")) return 409;
  // The id a create asked for is another row's; the same create cannot succeed
  // until the caller mints a new id.
  if (
    tag === "InspectionIdTaken" ||
    tag === "InspectionHasReport" ||
    tag === "InspectionHasReportHistory"
  )
    return 409;
  if (tag?.endsWith("NotFound")) return 404;
  return undefined;
};

/**
 * HTTP status implied by a contract failure. Uses `status` when the failure
 * carries one. Otherwise `Forbidden` is 403, a tag ending in `NotFound` is
 * 404, and a tag ending in `VersionConflict` or `Gone`, or
 * `InspectionIdTaken`, is 409.
 */
export const intentFailureStatus = (failure: unknown): number | undefined =>
  failureStatus(failure, failureTag(failure));

const intentFailureAsError = (failure: unknown): Error => {
  const tag = failureTag(failure);
  const message =
    failure instanceof Error && failure.message
      ? failure.message
      : (tag ?? "Intent failed");
  const error = new Error(message, { cause: failure });
  const status = failureStatus(failure, tag);
  if (status !== undefined) Object.assign(error, { status });
  return error;
};

/**
 * Runs an intent and resolves with an `Either`. An expected failure is the
 * `Left`. A defect or an interruption rejects the Promise.
 *
 * @deprecated Return the Effect from `runWebIntent`. Materialize an `Either`
 * where the caller branches. Pass that Effect to `queryResult` inside a
 * query function.
 */
export const runIntentResult = <A, E>(
  effect: Effect.Effect<A, E>,
): Promise<Either.Either<A, E>> =>
  Effect.runPromise(Effect.either(withRequestDiagnostics(effect)));

/**
 * Runs an intent and rejects with an `Error` for an expected failure. The
 * tagged failure is `error.cause`. `error.status` is the HTTP status carried
 * on the failure or implied by its `_tag`. A defect rejects unchanged.
 *
 * @deprecated Return the Effect from `runWebIntent`. Materialize an `Either`
 * where the caller branches. Pass that Effect to `queryResult` inside a
 * query function.
 */
export const runIntentAsError = async <A, E>(
  effect: Effect.Effect<A, E>,
): Promise<A> => {
  const exit = await Effect.runPromiseExit(effect);
  if (Exit.isSuccess(exit)) return exit.value;
  const failure = Cause.failureOption(exit.cause);
  if (Option.isSome(failure)) throw intentFailureAsError(failure.value);
  throw Cause.squash(exit.cause);
};

/** Stamps the HTTP status implied by a tagged contract failure onto that failure. */
export const withIntentStatus = <E>(failure: E): E => {
  if (typeof failure !== "object" || failure === null) return failure;
  if ("status" in failure) return failure;
  const status = intentFailureStatus(failure);
  if (status === undefined) return failure;
  return Object.assign(failure, { status });
};

const withStatus = withIntentStatus;

/**
 * Runs an intent and rejects with the contract's tagged failure. The
 * rejection carries the HTTP status implied by its `_tag`. A defect rejects
 * with its squashed cause.
 *
 * @deprecated Return the Effect from `runWebIntent`. Materialize an `Either`
 * where the caller branches. Pass that Effect to `queryResult` inside a
 * query function.
 */
export const runIntent = <A, E>(effect: Effect.Effect<A, E>): Promise<A> =>
  Effect.runPromiseExit(effect).then((exit) => {
    if (Exit.isSuccess(exit)) {
      return exit.value;
    }
    const failure = Cause.failureOption(exit.cause);
    throw Option.isSome(failure)
      ? withStatus(failure.value)
      : Cause.squash(exit.cause);
  });
